/**
 * File: ModernCtaSection.jsx
 * Path: /src/components/common/ModernCtaSection.jsx
 * Description: Reusable modern CTA section with animated background
 * Key Features: Gradient background, animated waves, and consistent branding
 */

import React from 'react';
import { motion } from 'framer-motion';
import Button from './Button';

const ModernCtaSection = ({ 
  title = "Ready to Revolutionize Your Legal Practice?", 
  description = "Join the growing number of forward-thinking law firms using Veritect.ai to gain a competitive edge.",
  buttonText = "Schedule a Demo",
  buttonLink = "/contact",
  children
}) => {
  return (
    <section className="relative py-20 overflow-hidden">
      {/* Main background with gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900 to-secondary-900 z-0"></div>
      
      {/* Wave pattern overlay */}
      <div className="absolute inset-0 opacity-20 z-0">
        <svg width="100%" height="100%" viewBox="0 0 1440 320" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          <path 
            d="M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,250.7C960,267,1056,245,1152,250.7C1248,256,1344,288,1392,304L1440,320L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            fill="url(#waveGradient)"
            strokeWidth="0"
            transform="rotate(180 720 160)"
            className="wave-animation-slow"
          />
          <path 
            d="M0,96L48,128C96,160,192,224,288,213.3C384,203,480,117,576,117.3C672,117,768,203,864,224C960,245,1056,203,1152,186.7C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            fill="url(#waveGradient)"
            strokeWidth="0"
            transform="rotate(180 720 160)"
            opacity="0.7"
            className="wave-animation-medium"
          />
        </svg>
      </div>
      
      {/* Floating decorative elements */}
      <div className="absolute inset-0 z-0">
        {/* Decorative circle 1 */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary-600/10 rounded-full blur-3xl animate-float"></div>
        
        {/* Decorative circle 2 */}
        <div className="absolute bottom-1/3 left-1/5 w-48 h-48 bg-secondary-600/10 rounded-full blur-3xl animate-float-delay-1"></div>
        
        {/* Decorative circle 3 */}
        <div className="absolute top-1/3 left-1/3 w-32 h-32 bg-accent-500/10 rounded-full blur-3xl animate-float-delay-2"></div>
      </div>
      
      {/* Content */}
      <div className="container-padding mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-6 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {title}
          </motion.h2>
          
          <motion.p 
            className="text-lg text-primary-100 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            {description}
          </motion.p>
          
          {children ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              {children}
            </motion.div>
          ) : (
            <motion.div 
              className="flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Button 
                variant="primary" 
                size="lg"
                to={buttonLink}
                className="bg-blue-600 hover:bg-blue-700 shadow-lg relative overflow-hidden group"
              >
                <span className="relative z-10">{buttonText}</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ModernCtaSection;